// The dashboard's chart colours, derived rather than chosen.
//
// **The language has no blue** (PLAN-UI §28.2), and the charts had four: a
// sparkline blue, a second blue for the "on for you" chips, the opponent's
// orange-red and an accent. Every one of them was a hex literal in
// `TunerPage.tsx`.
//
// What a chart needs that a palette of five tokens does not obviously give is
// *lines that can be told apart on one axis*. So they are derived: `brand` is
// the hero line, `cream` the live mark, `rust` the opposition, and the noisy
// offers line is brand sunk toward the secondary ink — near enough to read as
// the same measurement, far enough not to be mistaken for the record. A fifth
// line would be told apart by its **dash pattern** rather than by a fifth hue,
// which is what `SERIES_DASH` is for: hue is nearly exhausted at four and dash
// is not.
//
// `TOKENS` and not a theme, because these are handed to SVG `stroke` attributes
// and to `style`, where there is no theme to ask.

import { TOKENS } from "../kit";

const c = TOKENS.color;

/** Every line the charts draw, in the order they are read. */
export const SERIES_COLOUR = {
  /** What was offered. Brand, sunk toward the secondary ink: the noisy one. */
  offers: `color-mix(in srgb, ${c.brand} 55%, ${c.inkDim})`,
  /** The running best. The hero line, so the accent itself. */
  best: c.brand,
  /** The armies opposite. A control and never a target. */
  foe: c.rust,
  /** Where the islands are now. Bone, so it reads as a reading and not a series. */
  pending: c.cream
} as const;

/**
 * The dash each line is drawn with. Empty is solid.
 *
 * **This is the axis a fifth line would use.** Four hues is what this palette
 * has to spend before it starts inventing colours the rest of the interface
 * does not know, and two solid lines plus two patterns is already legible in
 * monochrome — which the hue never was.
 */
export const SERIES_DASH = {
  offers: "",
  best: "",
  foe: "3 3",
  pending: "2 4"
} as const;

/** Who won, in the language's own three voices. There is no green here. */
export const TEAM_TONE = {
  candidate: c.brand,
  stock: c.rust,
  draw: c.inkDim
} as const;

/** A term that pays, and a term that charges. Sign is the message. */
export const TERM_UP = c.brand;
export const TERM_DOWN = c.rust;

/** A fill at a fraction of a line's colour — a chip's ground, a bar's track. */
export const wash = (colour: string, percent: number): string =>
  `color-mix(in srgb, ${colour} ${percent}%, transparent)`;
